import { Select, TextInput, DateInput, TextAreaInput } from '@octano/global-ui';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import { Col, Row, Container } from 'reactstrap';
import { useValidations } from '../../../../../hooks/useValidations';
import { ClassroomEventFormFields } from '../types';
import { TimeModuleType } from '../../../../../types/timeModuleTypes';
import { useTexts } from '../hooks/useTexts';

interface ClassroomEventFormProps {
  modules: TimeModuleType[];
}

const ClassroomEventForm = ({ modules }: ClassroomEventFormProps) => {
  const texts = useTexts();
  const { msgValidations } = useValidations();

  const { control, watch } = useFormContext<ClassroomEventFormFields>();
  const [endModule] = watch(['startModule', 'endModule']);
  const startDate = watch('startDate');

  const startModuleOptions = useMemo(() => {
    return modules.map((module) => ({
      label: `${module.shortening} ${module.startTime}`,
      value: module.id,
    }));
  }, [modules]);

  const endModuleOptions = useMemo(() => {
    return modules.map((module) => ({
      label: `${module.shortening} ${module.endTime}`,
      value: module.id,
    }));
  }, [modules]);

  return (
    <Container>
      <Row className="mt-3">
        <Col xs="12" md="6" className="mt-2">
          <DateInput
            name="startDate"
            label={texts.form.startDate}
            control={control}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs="12" md="6" className="mt-2">
          <DateInput
            name="endDate"
            label={texts.form.endDate}
            control={control}
            rules={{
              required: msgValidations.required,
              validate: (value) => {
                if (!value || !startDate) {
                  return undefined;
                }
                // Se normaliza con dayjs y NO se comparan como string: el
                // DateInput entrega un Date, y `String(new Date())` empieza por
                // el día de la semana ("Sat…" < "Thu…"), así que la comparación
                // lexicográfica da cualquier cosa.
                return dayjs(value as string | Date).isBefore(
                  dayjs(startDate as string | Date),
                  'day',
                )
                  ? texts.form.dateRangeError
                  : undefined;
              },
            }}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs="12" md="6" className="mt-2">
          <Select
            name="startModule"
            label={texts.form.startTime}
            options={startModuleOptions}
            control={control}
            rules={{
              required: msgValidations.required,
              validate: (value) => {
                if (value && endModule) {
                  const startValue =
                    typeof value === 'string' ? value : value.value;
                  const endValue =
                    typeof endModule === 'string' ? endModule : endModule.value;
                  const indexStart = startModuleOptions.findIndex(
                    (mod) => mod.value === startValue,
                  );
                  const indexEnd = endModuleOptions.findIndex(
                    (mod) => mod.value === endValue,
                  );
                  if (
                    indexStart >= 0 &&
                    indexEnd >= 0 &&
                    indexStart > indexEnd
                  ) {
                    return msgValidations.inValidBeforeModule;
                  }
                }
                return undefined;
              },
            }}
          />
        </Col>
        <Col xs="12" md="6" className="mt-2">
          <Select
            name="endModule"
            label={texts.form.endTime}
            options={endModuleOptions}
            control={control}
            rules={{ required: msgValidations.required }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs="12" className="mt-2">
          <TextInput
            name="title"
            label={texts.form.title}
            control={control}
            rules={{
              required: msgValidations.required,
            }}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs="12" className="mt-2">
          <TextAreaInput
            name="description"
            label={texts.form.description}
            control={control}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ClassroomEventForm;
